"use client";

import * as React from "react";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";

export function useDebouncedOptions<T>({
  query,
  delay,
  fetcher,
}: {
  query: string;
  delay: number;
  fetcher: (search: string) => Promise<T[]>;
}) {
  const debouncedQuery = useDebouncedValue(query, delay);
  const [results, setResults] = React.useState<T[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      try {
        const data = await fetcher(debouncedQuery);
        if (cancelled) return;
        setResults(data);
        setError(null);
      } catch (err: any) {
        if (cancelled) return;
        setResults([]);
        setError(err?.message || "Failed to load results");
      } finally {
        if (cancelled) return;
        setLoading(false);
      }
    };
    void run();
    return () => {
      cancelled = true;
    };
  }, [debouncedQuery, fetcher, delay]);

  return { results, loading, error };
}
